import React from 'react';
import { Message } from './ChatInterface';

interface MessageBubbleProps {
  message: Message;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const isUser = message.role === 'user';
  
  // Function to parse inline markdown formatting
  const parseMarkdown = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;

    // Regular expressions for markdown patterns (order matters: bold before italic)
    const patterns = [
      { regex: /\*\*(.+?)\*\*/g, tag: 'strong' },  // **bold**
      { regex: /\*(.+?)\*/g, tag: 'em' },           // *italic*
      { regex: /_(.+?)_/g, tag: 'em' },             // _italic_
      { regex: /`(.+?)`/g, tag: 'code' },           // `code`
    ];

    let workingText = text;
    const allMatches: Array<{ start: number; end: number; content: string; tag: string }> = [];

    // Find all matches
    patterns.forEach(({ regex, tag }) => {
      const regexCopy = new RegExp(regex.source, regex.flags);
      let match;
      while ((match = regexCopy.exec(text)) !== null) {
        allMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          content: match[1],
          tag: tag,
        });
      }
    });

    // Sort matches by position, then by length (longer matches first to prefer **bold** over *italic*)
    allMatches.sort((a, b) => {
      if (a.start !== b.start) return a.start - b.start;
      return (b.end - b.start) - (a.end - a.start); // Longer matches first
    });

    // Filter out overlapping matches (keep first/longest match at each position)
    const matches: typeof allMatches = [];
    allMatches.forEach(match => {
      const overlaps = matches.some(existing =>
        (match.start >= existing.start && match.start < existing.end) ||
        (match.end > existing.start && match.end <= existing.end) ||
        (match.start <= existing.start && match.end >= existing.end)
      );
      if (!overlaps) {
        matches.push(match);
      }
    });

    // Build the formatted text
    if (matches.length === 0) {
      return text;
    }

    matches.forEach((match, idx) => {
      // Skip if this match is before our current position (shouldn't happen after filtering)
      if (match.end <= currentIndex) {
        return;
      }

      // Add text before the match
      if (match.start > currentIndex) {
        parts.push(text.substring(currentIndex, match.start));
      }

      // Add the formatted match
      if (match.tag === 'strong') {
        parts.push(<strong key={`strong-${idx}`}>{match.content}</strong>);
      } else if (match.tag === 'em') {
        parts.push(<em key={`em-${idx}`}>{match.content}</em>);
      } else if (match.tag === 'code') {
        parts.push(<code key={`code-${idx}`} className="app-message-inline-code">{match.content}</code>);
      }

      currentIndex = match.end;
    });

    // Add remaining text
    if (currentIndex < text.length) {
      parts.push(text.substring(currentIndex));
    }

    return parts;
  };

  // Function to format content with markdown-like features
  const formatContent = (content: string): React.ReactNode => {
    // Check if content includes structured data (tables, lists, etc.)
    if (message.metadata?.dataReturned) {
      return renderStructuredData(message.metadata.dataReturned, content);
    }

    // Split by newlines and process each line
    const lines = content.split('\n');
    return lines.map((line, index) => {
      // Handle bullet points (including markdown * bullets)
      if (line.startsWith('•') || line.startsWith('- ') || line.startsWith('* ')) {
        return (
          <div key={index} className="app-message-bullet">
            {parseMarkdown(line)}
          </div>
        );
      }

      // Handle numbered lists
      if (/^\d+\.\s/.test(line)) {
        return (
          <div key={index} className="app-message-bullet">
            {parseMarkdown(line)}
          </div>
        );
      }

      // Handle code blocks (simple detection)
      if (line.includes('```')) {
        return null; // Skip markdown code fence markers
      }

      // Handle headers (multiple # symbols)
      if (line.match(/^#{1,6}\s/)) {
        const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
        if (headerMatch) {
          const headerText = headerMatch[2];
          return (
            <div key={index} className="app-message-header">
              {parseMarkdown(headerText)}
            </div>
          );
        }
      }

      // Regular line
      return line ? (
        <div key={index} className="app-message-line">
          {parseMarkdown(line)}
        </div>
      ) : (
        <div key={index} className="app-message-spacer" />
      );
    });
  };

  // Render structured data like tables
  const renderStructuredData = (data: any, fallbackContent: string): React.ReactNode => {
    if (data.type === 'options_table' && Array.isArray(data.content)) {
      return (
        <div>
          <div className="app-message-line">{fallbackContent.split('\n')[0]}</div>
          <div className="app-message-table-container">
            <table className="app-message-table">
              <thead>
                <tr>
                  <th>Strike</th>
                  <th>Expiry</th>
                  <th>Delta</th>
                  <th>Premium</th>
                  <th>Ann. Yield</th>
                </tr>
              </thead>
              <tbody>
                {data.content.map((row: any, idx: number) => (
                  <tr key={idx}>
                    <td>${row.strike}</td>
                    <td>{row.expiry}</td>
                    <td>{row.delta?.toFixed(3)}</td>
                    <td>${row.premium?.toFixed(2)}</td>
                    <td>{row.annYield?.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (data.type === 'stock_list' && Array.isArray(data.content)) {
      return (
        <div>
          <div className="app-message-line">{fallbackContent.split('\n')[0]}</div>
          <div className="app-message-stock-grid">
            {data.content.map((stock: any, idx: number) => (
              <div key={idx} className="app-message-stock-item">
                <span className="app-message-stock-symbol">{stock.symbol}</span>
                <span className="app-message-stock-score">
                  Score: {stock.cc_score}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Fallback to regular content formatting
    return formatContent(fallbackContent);
  };

  return (
    <div className={`app-message-wrapper ${isUser ? 'app-message-user' : 'app-message-assistant'}`}>
      <div className="app-message-content">
        {/* Message bubble */}
        <div className={`app-message-bubble ${isUser ? 'app-message-user' : 'app-message-assistant'}`}>
          {formatContent(message.content)}
        </div>

        {/* Timestamp */}
        <div className="app-message-timestamp">
          {new Date(message.timestamp).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          })}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;